import {
  moneyCents,
  participantAmount,
  recalculateNet,
  sourceOptionistOptions,
} from './salesCommissionForm';
import { formWithResizedInstallments } from './salesCommissionInstallmentResize';
import {
  GROUP_CATALOG_KEYS,
  participantWithSelection,
  participantsWithDistributedPercentages,
  resizedParticipants,
  resizedPayers,
} from './salesCommissionParticipantForm';

export const formWithInstallment = (form, index, field, value) => ({
  ...form,
  installments: form.installments.map((row, rowIndex) =>
    rowIndex === index ? { ...row, [field]: value } : row
  ),
});

export const formWithPayer = (
  form,
  installmentIndex,
  payerIndex,
  field,
  value
) => {
  const installment = form.installments[installmentIndex];
  const payers = installment.payers.map((payer, index) =>
    index === payerIndex ? { ...payer, [field]: value } : payer
  );
  return formWithInstallment(form, installmentIndex, 'payers', payers);
};

export const formWithGroup = (form, group, field, value) => {
  const current = form.participants[group];
  const next = { ...current, [field]: value };
  if (field === 'total_percentage') {
    next.rows = current.rows.map(row => ({
      ...row,
      amount: participantAmount(
        moneyCents(form.net_amount),
        value,
        row.percentage
      ),
    }));
  }
  return {
    ...form,
    participants: { ...form.participants, [group]: next },
  };
};

export const salesCommissionMutations = ({ form, catalog, errors }) => {
  const participantOptionsFor = group => {
    const catalogOptions = catalog.value[GROUP_CATALOG_KEYS[group]] || [];
    if (group !== 'optionist_brokers') return catalogOptions;

    const options = [
      ...catalogOptions,
      ...sourceOptionistOptions(form.value.source_optionists),
    ];
    return options.filter(
      (option, index) =>
        options.findIndex(item => String(item.id) === String(option.id)) ===
        index
    );
  };

  const updateCompany = (field, value) => {
    form.value = recalculateNet({ ...form.value, [field]: value });
    errors.value = {};
  };

  const changeInstallmentCount = count => {
    form.value = formWithResizedInstallments(form.value, count);
    errors.value = {};
  };

  const updateInstallment = (index, field, value) => {
    form.value = formWithInstallment(form.value, index, field, value);
  };

  const updatePayer = (installmentIndex, payerIndex, field, value) => {
    form.value = formWithPayer(
      form.value,
      installmentIndex,
      payerIndex,
      field,
      value
    );
  };

  const setPayerCount = (installmentIndex, count) => {
    const installment = form.value.installments[installmentIndex];
    updateInstallment(
      installmentIndex,
      'payers',
      resizedPayers(installment, count)
    );
  };

  const updateGroup = (group, field, value) => {
    form.value = formWithGroup(form.value, group, field, value);
  };

  const toggleParticipantGroup = (group, enabled) => {
    updateGroup(group, 'enabled', enabled);
    if (!enabled) updateGroup(group, 'rows', []);
    errors.value = {};
  };

  const updateOptionistReport = reportType => {
    const report = catalog.value.tipo_laudo_opcionista?.find(
      item => String(item.id) === String(reportType)
    );
    updateGroup('optionist_brokers', 'report_type', reportType);
    updateGroup('optionist_brokers', 'total_percentage', report?.percent || '');
    errors.value = {};
  };

  const distributeParticipantPercentages = group => {
    const current = form.value.participants[group];
    updateGroup(
      group,
      'rows',
      participantsWithDistributedPercentages({
        rows: current.rows,
        netCents: moneyCents(form.value.net_amount),
        totalPercentage: current.total_percentage,
      })
    );
    errors.value = {};
  };

  const setParticipantCount = (group, count) => {
    const current = form.value.participants[group];
    updateGroup(group, 'rows', resizedParticipants(current, count));
  };

  const updateParticipant = (group, index, field, value) => {
    const current = form.value.participants[group];
    const rows = current.rows.map((row, rowIndex) => {
      if (rowIndex !== index) return row;
      return participantWithSelection({
        row,
        options: participantOptionsFor(group),
        netCents: moneyCents(form.value.net_amount),
        totalPercentage: current.total_percentage,
        field,
        value,
      });
    });
    updateGroup(group, 'rows', rows);
  };

  return {
    changeInstallmentCount,
    setParticipantCount,
    setPayerCount,
    toggleParticipantGroup,
    updateCompany,
    updateGroup,
    updateInstallment,
    updateOptionistReport,
    updateParticipant,
    updatePayer,
    distributeParticipantPercentages,
  };
};
